import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  LogIn,
  Loader2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogin } from "@react-oauth/google";

import { AuthContext } from "../../context/AuthContext";
import { BASE_URL } from "../../config/api";
import AuthLayout from "../../Component/Auth/AuthLayout";
import GlassCard from "../../Component/Auth/GlassCard";
import toast from "react-hot-toast";
import AuthHeader from "../../Component/Auth/AuthHeader";

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useContext(AuthContext);

  const IDENTIFIER_KEY = "lastLoginIdentifier";

  const [authMode, setAuthMode] = useState("otp"); // password | otp | mpin
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({ id: "", pwd: "" });
  const [submitting, setSubmitting] = useState(false);

   const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const [forceLogoutPopup, setForceLogoutPopup] = useState(false);


  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);

  const [mpin, setMpin] = useState("");
  const [showSetMpinModal, setShowSetMpinModal] = useState(false);
  const [newMpin, setNewMpin] = useState(["", "", "", ""]);
  const [confirmMpin, setConfirmMpin] = useState(["", "", "", ""]);
  const [settingMpin, setSettingMpin] = useState(false);
  const [isConfirmStep, setIsConfirmStep] = useState(false);
  const [mpinAttempts, setMpinAttempts] = useState(0);
  const [mpinLocked, setMpinLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  const [mpinLoading, setMpinLoading] = useState(false);

  const [forgotMpinModal, setForgotMpinModal] = useState(false);
  const [forgotOtp, setForgotOtp] = useState(["", "", "", "", "", ""]);
  const forgotOtpRefs = useRef([]);
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotOtpTimer, setForgotOtpTimer] = useState(0);

  const [forgotLoginValue, setForgotLoginValue] = useState("");
  const [forgotOtpLoading, setForgotOtpLoading] = useState(false);

  const newRefs = useRef([]);
  const confirmRefs = useRef([]);

  const [activeSession, setActiveSession] = useState(false);
  const [hasStoredIdentifier, setHasStoredIdentifier] = useState(false);
  const businessName = localStorage.getItem("business_name");

useEffect(() => {
  const savedIdentifier = localStorage.getItem("lastLoginIdentifier");
  const isMpinActive = localStorage.getItem("isMpinActive");

  // FIRST LOGIN → PASSWORD
  if (!savedIdentifier) {
    setAuthMode("otp");
    return;
  }

  // SECOND LOGIN
  setIdentifier(savedIdentifier);
  setHasStoredIdentifier(true);

  if (isMpinActive === "1") {
    // MPIN exists
    setAuthMode("mpin");
  } else {
    // MPIN not set
    setAuthMode("otp");
  }
}, []);
  // =========================
  // OTP TIMER
  // =========================
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => {
      setOtpTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  // =========================
  // SESSION CHECK
  // =========================
  const checkSession = async () => {
    if (!identifier) return;
    try {
      const res = await axios.get(
        `${BASE_URL}common/check-session/${identifier}`
      );
      setActiveSession(res.data?.active === true);
    } catch {}
  };

  const handleOtpChange = (e, index) => {
  const value = e.target.value.replace(/\D/g, "");

  if (!value) return;

  const updatedOtp = [...otp];
  updatedOtp[index] = value[0];
  setOtp(updatedOtp);

  if (index < 5) {
    otpRefs.current[index + 1]?.focus();
  }
};

const handleOtpKeyDown = (e, index) => {
  if (e.key === "Backspace") {
    const updatedOtp = [...otp];

    if (updatedOtp[index]) {
      updatedOtp[index] = "";
    } else if (index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    setOtp(updatedOtp);
  }
};

const handleEditIdentifier = () => {
  setOtpSent(false);
  setOtp(["", "", "", "", "", ""]);
};


const handleOtpPaste = (e) => {
  const pastedData = e.clipboardData.getData("text").trim();

  if (!/^\d{6}$/.test(pastedData)) return;

  const otpArray = pastedData.split("");
  setOtp(otpArray);

  otpArray.forEach((digit, index) => {
    if (otpRefs.current[index]) {
      otpRefs.current[index].value = digit;
    }
  });

  // focus last box
  otpRefs.current[5]?.focus();
};


 const handleBoxChange = (e, index, type = "new") => {
  const value = e.target.value.replace(/\D/g, "");

  if (!value) return;

  const updated =
    type === "new" ? [...newMpin] : [...confirmMpin];

  updated[index] = value[0];

  type === "new"
    ? setNewMpin(updated)
    : setConfirmMpin(updated);

  // Move to next input
  if (index < 3) {
    const nextRef =
      type === "new"
        ? newRefs.current[index + 1]
        : confirmRefs.current[index + 1];

    nextRef?.focus();
  }
};

const handleKeyDown = (e, index, type = "new") => {
  if (e.key === "Backspace") {
    const updated =
      type === "new" ? [...newMpin] : [...confirmMpin];

    if (updated[index]) {
      updated[index] = "";
    } else if (index > 0) {
      const prevRef =
        type === "new"
          ? newRefs.current[index - 1]
          : confirmRefs.current[index - 1];

      prevRef?.focus();
    }

    type === "new"
      ? setNewMpin(updated)
      : setConfirmMpin(updated);
  }
};

  // =========================
  // REDIRECT
  // =========================
  // const redirectBasedOnRole = (userData) => {
  //   const role = Number(userData.role);
  //   const routes = {
  //     1: "/superdashboard",
  //     2: "/dashboard",
  //     3: "/empdashboard",
  //     4: "/agentdashboard",
  //     5: "/retailerdashboard",
  //     6: "/userdashboard",
  //   };
  //   navigate(routes[role] || "/dashboard");
  // };

    const getRoleRoute = (role) => {
    const map = {
      1: "admin",
      2: "company",
      4: "agent",
      3: "employee",
      5: "retailer",
      6: "retailer_user",
    };
    return map[Number(role)] || "company";
  };

  const redirectBasedOnRole = (userData) => {
    const role = Number(userData.role);
    const type = userData.type_of_user;

    // 🔥 SPECIAL CASE
    if (role === 3) {
      const map = {
        Operation: "/oprdashboard",
        Sales: "/empdashboard",
      };
      navigate(map[type] || "/empdashboard");
      return;
    }

    // 🔁 DEFAULT ROLE ROUTES
    const routes = {
      1: "/superdashboard",
      2: "/dashboard",
      4: "/agentdashboard",
      3: "/empdashboard",
      5: "/retailerdashboard",
      6: "/userdashboard",
    };

    navigate(routes[role] || "/dashboard");
  };


  // useEffect(() => {
  //   if (user && user.role) {
  //     redirectBasedOnRole(getRoleRoute(user.role));
  //   }
  // }, [user]);

  const validate = () => {
    let ok = true;
    let idErr = "";
    let pwdErr = "";

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const phoneRe = /^[0-9]{10}$/;

    if (!identifier.trim()) {
      idErr = "Enter email or phone";
      ok = false;
    } else if (!(emailRe.test(identifier) || phoneRe.test(identifier))) {
      idErr = "Use valid email or 10-digit phone";
      ok = false;
    }

    if (!password) {
      pwdErr = "Enter password";
      ok = false;
    } else if (password.length < 3) {
      pwdErr = "Min 3 characters";
      ok = false;
    }

    setErrors({ id: idErr, pwd: pwdErr });
    return ok;
  };

  // =========================
  // PASSWORD LOGIN
  // =========================
 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  setSubmitting(true);
  try {
    const res = await axios.post(`${BASE_URL}common/login`, {
      email: identifier,
      password,
    });

    const userData = res.data.data;

    // 🔍 CHECK FORCE LOGOUT STATUS
    // 🔍 CHECK FORCE LOGOUT STATUS (ONLY FOR RETAILER)
      if (Number(userData.role) === 5) {
        const statusRes = await axios.get(
          `${BASE_URL}common/logout-status/${userData.id}`
        );

        const isForceLogout =
          statusRes.data?.is_logout === true ||
          statusRes.data?.is_logout === 1;

        if (statusRes.data?.status === true && isForceLogout) {
          setForceLogoutPopup(true);
          return;
        }
      }

    // ✅ ALLOW LOGIN
    login(userData);

    localStorage.setItem("primaryCompanyId", userData.id);
    localStorage.setItem("lastLoginIdentifier", identifier);
    localStorage.setItem("isMpinActive", userData.is_mpin_active);
    localStorage.setItem("just_logged_in", "true");
    localStorage.setItem("business_name", userData.business_name);

    redirectBasedOnRole(userData);

  } catch (err) {
    toast.error(err?.response?.data?.message || "Login failed");
  } finally {
    setSubmitting(false);
  }
};

const handleForgotPassword = async () => {
  if (!forgotEmail.trim()) {
    setForgotMsg("Please enter your email");
    return;
  }

  setForgotLoading(true);
  setForgotMsg("");

  try {
    await axios.post(`${BASE_URL}warranty/company/password/forgot`, {
      email: forgotEmail,
    });

    // ✅ SUCCESS TOAST
    toast.success("Reset mail sent successfully");

    // ✅ CLOSE MODAL
    setForgotOpen(false);
    setForgotEmail("");
    setForgotMsg("");

  } catch (err) {
    setForgotMsg(
      err?.response?.data?.message || "Failed to send reset link"
    );
  } finally {
    setForgotLoading(false);
  }
};

  // =========================
  // OTP
  // =========================
  const sendOtp = async () => {
  if (!identifier) return toast.error("Enter email");

  if (otpTimer > 0) return; // prevent spam

  setOtpLoading(true);

  try {
    await axios.post(`${BASE_URL}common/send-otp`, {
      login_value: identifier,
    });

    toast.success("OTP sent successfully");

    setOtpTimer(60);
    setOtpSent(true);

    setTimeout(() => {
      otpRefs.current[0]?.focus();
    }, 200);

  } catch (err) {
    toast.error("Failed to send OTP");
  } finally {
    setOtpLoading(false);
  }
};

  const verifyOtp = async () => {
  try {

    const otpValue = otp.join("");
    const res = await axios.post(`${BASE_URL}common/verify-otp`, {
      login_value: identifier,
      otp: otpValue,
    });

    const userData = res.data.data;

    login(userData);

    localStorage.setItem("primaryCompanyId", userData.id);
    localStorage.setItem("lastLoginIdentifier", identifier);
    localStorage.setItem("isMpinActive", userData.is_mpin_active);
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("just_logged_in", "true");
    localStorage.setItem("business_name", userData.business_name);

    // ✅ If MPIN already exists → go directly
    redirectBasedOnRole(userData);

  } catch {
    toast.error("Invalid OTP");
  }
};

useEffect(() => {
  const otpValue = otp.join("");

  if (otpValue.length === 6 && otpSent && !submitting) {
    verifyOtp();
  }
}, [otp]);

  // =========================
  // MPIN
  // =========================
  

  const handleSubmitMpin = async (e) => {
  e?.preventDefault();

  if (settingMpin) return; // 🚫 prevent double call

  const mpinValue = newMpin.join("");
  const confirmValue = confirmMpin.join("");

  if (mpinValue.length !== 4)
    return toast.error("Enter 4 digit MPIN");

  if (confirmValue.length !== 4)
    return toast.error("Confirm your MPIN");

  if (mpinValue !== confirmValue)
    return toast.error("MPIN does not match");

  const primaryCompanyId = localStorage.getItem("primaryCompanyId");

  if (!primaryCompanyId) {
    toast.error("Session expired. Please login again.");
    return;
  }

  try {
    setSettingMpin(true);

    const res = await axios.post(`${BASE_URL}common/set-mpin`, {
      company_id: primaryCompanyId,
      m_pin: mpinValue,
    });

    if (res?.data?.status) {
      toast.success(res.data.message);

      localStorage.setItem("isMpinActive", "1");
      localStorage.setItem("business_name", res.data.business_name);

      setShowSetMpinModal(false);
      setAuthMode("mpin");

    }

  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to set MPIN");
  } finally {
    setSettingMpin(false);
  }
};

useEffect(() => {
  if (lockTimer <= 0) return;

  const interval = setInterval(() => {
    setLockTimer((prev) => prev - 1);
  }, 1000);

  return () => clearInterval(interval);
}, [lockTimer]);

const handleMpinLogin = async () => {
  const savedPrimaryId = localStorage.getItem("primaryCompanyId");

  if (!savedPrimaryId)
    return toast.error("Session expired. Login with password");

  if (mpin.length !== 4)
    return toast.error("Enter 4 digit MPIN");

  try {
    setMpinLoading(true);

    const res = await axios.post(
      `${BASE_URL}common/login-mpin`,
      {
        company_id: savedPrimaryId, // ✅ using stored primary ID
        m_pin: mpin,
      }
    );

    login(res.data.data);
    redirectBasedOnRole(res.data.data);
    localStorage.setItem("business_name", res.data.data.business_name);

  } catch (err) {
    handleMpinError(err?.response?.data);
  } finally {
    setMpinLoading(false);
  }
};

const handleMpinError = (data) => {
  const attemptsLeft = data?.attempts_left ?? 0;

  if (data?.lock_time) {
    setMpinLocked(true);
    setLockTimer(data.lock_time);
    toast.error(`Account locked for ${data.lock_time} seconds`);
    return;
  }

  setMpinAttempts((prev) => prev + 1);

  toast.error(
    attemptsLeft > 0
      ? `Invalid MPIN. ${attemptsLeft} attempts left`
      : "Invalid MPIN"
  );

  setMpin("");
};

  // =========================
  // GOOGLE LOGIN
  // =========================
  const handleGoogleLogin = async (credentialResponse) => {
  try {
    const token = credentialResponse?.credential;

    if (!token) {
      toast.error("Google token missing");
      return;
    }

    const res = await axios.post(
      `${BASE_URL}warranty/company/auth/google`,
      { token }
    );

    if (res.data?.status) {
      login(res.data.data);
      redirectBasedOnRole(res.data.data);
      localStorage.setItem("business_name", res.data.data.business_name);
    } else {
      toast.error(res.data?.message || "Google login failed");
    }
  } catch (err) {
    toast.error(
      err?.response?.data?.message || "Google login failed"
    );
  }
};

useEffect(() => {
  if (forgotMpinModal) {
    const savedIdentifier = localStorage.getItem("lastLoginIdentifier");
    if (savedIdentifier) {
      setForgotLoginValue(savedIdentifier);
    }
  }
}, [forgotMpinModal]);

useEffect(() => {
  if (forgotOtpTimer <= 0) return;

  const interval = setInterval(() => {
    setForgotOtpTimer((prev) => prev - 1);
  }, 1000);

  return () => clearInterval(interval);
}, [forgotOtpTimer]);

const sendForgotMpinOtp = async () => {
  if (!forgotLoginValue.trim()) {
    toast.error("Enter phone or email");
    return;
  }

  if (forgotOtpTimer > 0) return;

  try {
    setForgotOtpLoading(true);

    await axios.post(`${BASE_URL}common/send-otp`, {
      login_value: forgotLoginValue,
    });

    toast.success("OTP sent successfully");

    setForgotOtpSent(true);
    setForgotOtpTimer(60); // start timer

    setTimeout(() => {
      forgotOtpRefs.current[0]?.focus();
    }, 200);

  } catch {
    toast.error("Failed to send OTP");
  } finally {
    setForgotOtpLoading(false);
  }
};

const handleEditForgotIdentifier = () => {
  setForgotOtpSent(false);
  setForgotOtp(["", "", "", "", "", ""]);
  setForgotOtpTimer(0);
};

const handleForgotOtpChange = (e, index) => {
  const value = e.target.value.replace(/\D/g, "");

  if (!value) return;

  const updated = [...forgotOtp];
  updated[index] = value[0];
  setForgotOtp(updated);

  if (index < 5) {
    forgotOtpRefs.current[index + 1]?.focus();
  }
};

const handleForgotOtpKeyDown = (e, index) => {
  if (e.key === "Backspace") {
    const updated = [...forgotOtp];

    if (updated[index]) {
      updated[index] = "";
    } else if (index > 0) {
      forgotOtpRefs.current[index - 1]?.focus();
    }

    setForgotOtp(updated);
  }
};

const verifyForgotMpinOtp = async () => {
  try {

    const otpValue = forgotOtp.join("");

    await axios.post(`${BASE_URL}common/verify-otp`, {
      login_value: forgotLoginValue,
      otp: otpValue,
    });

    toast.success("OTP verified");

    setForgotMpinModal(false);
    setShowSetMpinModal(true);

  } catch {
    toast.error("Invalid OTP");
  }
};

useEffect(() => {
  const value = forgotOtp.join("");

  if (value.length === 6) {
    verifyForgotMpinOtp();
  }
}, [forgotOtp]);


  const loginModes = !hasStoredIdentifier
    ? ["otp", "password" ]
    : localStorage.getItem("isMpinActive") === "1"
    ? ["mpin", "otp"]
    : ["otp"];

  return (
    <AuthLayout showIllustration={true}>
      <GlassCard className="w-full max-w-xl mx-auto px-6 py-8 sm:px-8 sm:py-10 backdrop-blur-xl bg-white/70 shadow-2xl border border-white/40">
      <AuthHeader
//           // title="Login"
//           // subtitle="Access your dashboard"
        />
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-slate-800">
            {businessName ? "Welcome Back" : "Welcome"}
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            {businessName || "Sign in to continue"}
          </p>
        </div>

        {/* AUTH MODE TABS */}
       {loginModes.length > 1 && (
          <div className="mb-6 flex rounded-xl bg-slate-100 p-1">
            {loginModes.map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  setAuthMode(mode);
                  setOtpSent(false);
                  setOtp(["", "", "", "", "", ""]);
                }}
                className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${
                  authMode === mode
                    ? "bg-white shadow text-blue-600"
                    : "text-slate-500"
                }`}
              >
                {mode.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* IDENTIFIER */}
         {authMode !== "mpin" && !otpSent && (
            <div>
              <div className="relative rounded-xl bg-white border px-3 focus-within:ring-2 focus-within:ring-blue-400">
                <span className="absolute left-3 top-3 text-slate-400">
                  {/^[0-9]/.test(identifier) ? (
                    <Phone size={16} />
                  ) : (
                    <Mail size={16} />
                  )}
                </span>
                <input
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  onBlur={checkSession}
                  placeholder="Email or phone"
                  className="w-full bg-transparent pl-6 py-3 text-sm outline-none"
                />
              </div>

              {activeSession && (
                <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  Active session detected on another device
                </div>
              )}
            </div>
          )}

          {/* ANIMATED AUTH MODES */}
          <AnimatePresence mode="wait">
            <motion.div
              key={authMode}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {authMode === "password" && (
                <>
                  <div className="relative rounded-xl bg-white border px-3">
                    <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full pl-6 pr-8 py-3 outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-2 top-2"
                    >
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-xl py-3 font-semibold text-white bg-gradient-to-r from-blue-500 via-cyan-500 to-green-400 hover:scale-[1.02] transition-all mt-3"
                  >
                    {submitting ? "Logging in..." : "Login"}
                  </button>

                  <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotOpen(true);
                      setForgotMsg("");
                      setForgotEmail("");
                    }}
                    className="text-xs font-semibold text-blue-600 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                </>
              )}

              {authMode === "otp" && (
                <>
                  {!otpSent ? (
                    <>
                    
                    <button
                      type="button"
                      onClick={sendOtp}
                      disabled={otpLoading || otpTimer > 0}
                      className="w-full rounded-xl py-2 bg-gradient-to-r from-blue-500 via-cyan-500 to-green-400 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {otpLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending OTP...
                        </>
                      ) : otpTimer > 0 ? (
                        `Resend in ${otpTimer}s`
                      ) : (
                        "Send OTP"
                      )}
                    </button>

                    {/* SET MPIN OPTION */}
                    {/* {hasStoredIdentifier && localStorage.getItem("isMpinActive") !== "1" && (
                      <div className="text-center mt-3">
                        <button
                          type="button"
                          onClick={() => {
                            const savedIdentifier = localStorage.getItem("lastLoginIdentifier");
                            setForgotLoginValue(savedIdentifier || identifier);
                            setForgotOtpSent(false);
                            setForgotOtp(["", "", "", "", "", ""]);
                            setForgotOtpTimer(0);
                            setForgotMpinModal(true);
                          }}
                          className="text-sm font-semibold  hover:underline"
                        >
                          Want to set MPIN?
                        </button>
                      </div>
                    )} */}
                    {/* <div className="text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setForgotOpen(true);
                            setForgotMsg("");
                            setForgotEmail("");
                          }}
                          className="text-xs font-semibold text-blue-600 hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div> */}
                      </>
                  ) : (
                    /* STEP 2: SHOW VERIFY OTP */
                    <>

                    {otpSent && (
                      <div className="flex items-center justify-between bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg mb-3">
                        <div>
                          <p className="text-xs text-slate-600">
                          OTP sent to <span className="font-semibold">{identifier} </span>
                        </p>
                        
                        <p className="text-xs text-slate-600">
                          <span>please Check Whatsapp or Mail </span>
                        </p>
                        </div>

                        <button
                          type="button"
                          onClick={handleEditIdentifier}
                          className="text-xs font-semibold text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                      <div className="flex justify-center gap-3 mb-4">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => (otpRefs.current[index] = el)}
                            type="tel"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(e, index)}
                            onKeyDown={(e) => handleOtpKeyDown(e, index)}
                            onPaste={handleOtpPaste}
                            autoComplete="one-time-code"
                            className="w-9 h-9 border border-slate-300 rounded-lg text-center text-md font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                          />
                        ))}
                      </div>

                      {/* <button
                        type="button"
                        onClick={verifyOtp}
                        className="w-full mt-3 rounded-xl bg-blue-600 py-3 text-white font-semibold hover:bg-blue-700 transition"
                      >
                        {ismail=== 1 ? "Verify Mail OTP" : "Verify WA OTP"}
                        Verify OTP
                      </button> */}

                      {/* RESEND TIMER */}
                      <div className="text-xs text-slate-500 mt-3 text-center">
                        {otpTimer > 0 ? (
                          <span>Resend OTP in {otpTimer}s</span>
                        ) : (
                          <button
                            type="button"
                            onClick={sendOtp}
                            disabled={otpTimer > 0}
                            className="text-blue-600 font-semibold hover:underline disabled:text-gray-400"
                          >
                            {otpTimer > 0 ? `Resend in ${otpTimer}s` : "Resend OTP"}
                          </button>
                        )}
                      </div>
                      
                    </>
                  )}
                </>
              )}

              {authMode === "mpin" && (
                <>
                  {/* 4 Digit MPIN Boxes */}
                  <div className="flex justify-center gap-4 mb-4">
                  {[0, 1, 2, 3].map((index) => (
                    <input 
                      key={index}
                      id={`mpin-${index}`}
                      type="password"
                      maxLength={1}
                      inputMode="numeric"
                      value={mpin[index] || ""}

                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");

                        const updated = mpin.split("");

                        // allow clearing
                        if (!value) {
                          updated[index] = "";
                          setMpin(updated.join(""));
                          return;
                        }

                        updated[index] = value[0];
                        setMpin(updated.join(""));

                        // move next
                        if (index < 3) {
                          document
                            .getElementById(`mpin-${index + 1}`)
                            ?.focus();
                        }
                      }}

                      onKeyDown={(e) => {
                        if (e.key === "Backspace") {

                          const updated = mpin.split("");

                          // if current has value → clear only
                          if (updated[index]) {
                            updated[index] = "";
                            setMpin(updated.join(""));
                          }

                          // if empty → move previous
                          else if (index > 0) {
                            document
                              .getElementById(`mpin-${index - 1}`)
                              ?.focus();
                          }
                        }
                      }}

                      className="
                        w-12 h-12
                        border border-slate-300
                        rounded-xl
                        text-center
                        text-2xl
                        font-semibold
                        focus:border-blue-500
                        focus:ring-2
                        focus:ring-blue-200
                        outline-none
                      "
                    />
                  ))}
                </div>

                  {mpinLocked && (
                    <p className="text-sm text-rose-600 text-center mb-2">
                      Account locked. Try after {lockTimer}s
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleMpinLogin}
                    disabled={mpinLoading || mpinLocked}
                    className="w-full rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-green-400 py-3 text-white font-semibold disabled:opacity-50"
                  >
                    {mpinLoading ? "Verifying..." : "Login with MPIN"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setForgotMpinModal(true)}
                    className="mt-3 text-sm text-blue-600 font-semibold hover:underline w-full text-center"
                  >
                    Forgot MPIN?
                  </button>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </form>

        {/* DIVIDER */}
        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">
            OR CONTINUE WITH
          </span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* CUSTOM GOOGLE BUTTON */}
        <GoogleLogin
          onSuccess={handleGoogleLogin}
          onError={() => toast.error("Google login failed")}
          useOneTap
        />
      </GlassCard>

      {forgotOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
              
              <h3 className="text-lg font-semibold text-slate-800">
                Forgot Password
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Enter your registered email to receive a reset link
              </p>

              <div className="mt-4">
                <label className="text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="
                    mt-1 w-full rounded-xl border border-slate-200
                    px-3 py-2 text-sm outline-none
                    focus:ring-2 focus:ring-blue-400
                  "
                />
              </div>

              {forgotMsg && (
                <p
                  className={`mt-2 text-xs font-semibold ${
                    forgotMsg.includes("sent")
                      ? "text-emerald-600"
                      : "text-rose-500"
                  }`}
                >
                  {forgotMsg}
                </p>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="
                    rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white
                    hover:bg-blue-700 disabled:opacity-60
                  "
                >
                  {forgotLoading ? "Sending..." : "Send link"}
                </button>
              </div>
            </div>
          </div>
        )}

      {forceLogoutPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
            <h2 className="text-lg font-bold text-rose-700">
              Login Restricted
            </h2>

            <p className="mt-2 text-sm font-semibold text-slate-600">
              You have been <span className="font-bold text-rose-600">force logged out</span> by your company.
              <br />
              Please contact support for assistance.
            </p>

            <button
              onClick={() => setForceLogoutPopup(false)}
              className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showSetMpinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="relative bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl">

            {/* CLOSE BUTTON */}
            <button
              onClick={() => setShowSetMpinModal(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 text-xl font-bold"
            >
              ✕
            </button>

            {/* Title */}
            <h2 className="text-center text-xl font-semibold text-slate-800">
              Set Your MPIN
            </h2>

            {/* ENTER MPIN */}
            <div className="mt-8">
              <p className="text-sm text-slate-600 mb-4 text-center">
                Enter MPIN
              </p>

              <div className="flex justify-center gap-4">
                {newMpin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (newRefs.current[index] = el)}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleBoxChange(e, index, "new")}
                    onKeyDown={(e) => handleKeyDown(e, index, "new")}
                    className="w-14 h-14 border border-slate-300 rounded-lg text-center text-xl font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  />
                ))}
              </div>
            </div>

            {/* CONFIRM MPIN */}
            <div className="mt-8">
              <p className="text-sm text-slate-600 mb-4 text-center">
                Confirm MPIN
              </p>

              <div className="flex justify-center gap-4">
                {confirmMpin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (confirmRefs.current[index] = el)}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleBoxChange(e, index, "confirm")}
                    onKeyDown={(e) => handleKeyDown(e, index, "confirm")}
                    className="w-14 h-14 border border-slate-300 rounded-lg text-center text-xl font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  />
                ))}
              </div>
            </div>

            {/* BUTTON */}
            <button
              type="button"
              onClick={handleSubmitMpin}
              disabled={settingMpin}
              className="mt-10 w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-60"
            >
              {settingMpin ? "Setting MPIN..." : "Set MPIN"}
            </button>
          </div>
        </div> 
      )}

      {forgotMpinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl">

            <h2 className="text-lg font-semibold text-center">
              Verify Phone OTP
            </h2>

            {/* INPUT OR SENT MESSAGE */}
            {!forgotOtpSent ? (
              <input
                type="text"
                value={forgotLoginValue}
                onChange={(e) => setForgotLoginValue(e.target.value)}
                placeholder="Enter phone or email"
                className="w-full border rounded-lg px-3 py-3 text-md outline-none focus:ring-2 focus:ring-blue-400 mt-4"
              />
            ) : (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg mt-4">
                <p className="text-xs text-slate-600">
                  OTP sent to <span className="font-semibold">{forgotLoginValue}</span>
                </p>

                <button
                  type="button"
                  onClick={handleEditForgotIdentifier}
                  className="text-xs font-semibold text-blue-600 hover:underline"
                >
                  Edit
                </button>
              </div>
            )}

            {!forgotOtpSent ? (
              <button
                onClick={sendForgotMpinOtp}
                disabled={forgotOtpLoading || forgotOtpTimer > 0}
                className="mt-6 w-full bg-blue-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {forgotOtpLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : forgotOtpTimer > 0 ? (
                  `Resend in ${forgotOtpTimer}s`
                ) : (
                  "Send OTP"
                )}
              </button>
              ) : (
                <>
                  <div className="flex justify-center gap-3 mt-6">
                    {forgotOtp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (forgotOtpRefs.current[index] = el)}
                        type="tel"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleForgotOtpChange(e, index)}
                        onKeyDown={(e) => handleForgotOtpKeyDown(e, index)}
                        className="w-10 h-10 border rounded-lg text-center"
                      />
                    ))}
                  </div>

                  {/* RESEND TIMER */}
                  <div className="text-xs text-slate-500 mt-3 text-center">
                    {forgotOtpTimer > 0 ? (
                      <span>Resend OTP in {forgotOtpTimer}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={sendForgotMpinOtp}
                        disabled={forgotOtpLoading}
                        className="text-blue-600 font-semibold hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        {forgotOtpLoading ? "Sending..." : "Resend OTP"}
                      </button>
                    )}
                  </div>
                </>
              )}

            <button
              onClick={() => setForgotMpinModal(false)}
              className="mt-6 w-full border rounded-lg py-2"
            >
              Cancel
            </button>

          </div>
        </div>
      )}
    </AuthLayout>
  );
}


// import React, { useState, useEffect, useContext, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import {
//   Eye,
//   EyeOff,
//   Lock,
//   Mail,
//   Phone,
//   LogIn,
//   Loader2,
// } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import { GoogleLogin } from "@react-oauth/google";

// import { AuthContext } from "../../context/AuthContext";
// import { BASE_URL } from "../../config/api";
// import AuthLayout from "../../Component/Auth/AuthLayout";
// import GlassCard from "../../Component/Auth/GlassCard";
// import toast from "react-hot-toast";
// import AuthHeader from "../../Component/Auth/AuthHeader";

// export default function Login() {
//   const navigate = useNavigate();
//   const { login, user } = useContext(AuthContext);

//   const [authMode, setAuthMode] = useState("password"); // password | otp | mpin
//   const [identifier, setIdentifier] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [errors, setErrors] = useState({ id: "", pwd: "" });
//   const [submitting, setSubmitting] = useState(false);

//    const [forgotOpen, setForgotOpen] = useState(false);
//   const [forgotEmail, setForgotEmail] = useState("");
//   const [forgotLoading, setForgotLoading] = useState(false);
//   const [forgotMsg, setForgotMsg] = useState("");

//   const [forceLogoutPopup, setForceLogoutPopup] = useState(false);


//   const [otp, setOtp] = useState("");
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpTimer, setOtpTimer] = useState(0);
//   const [otpLoading, setOtpLoading] = useState(false);

//   const [mpin, setMpin] = useState("");
//   const [showSetMpinModal, setShowSetMpinModal] = useState(false);
//   const [newMpin, setNewMpin] = useState(["", "", "", ""]);
//   const [confirmMpin, setConfirmMpin] = useState(["", "", "", ""]);
//   const [isConfirmStep, setIsConfirmStep] = useState(false);
//   const [mpinAttempts, setMpinAttempts] = useState(0);
//   const [mpinLocked, setMpinLocked] = useState(false);
//   const [lockTimer, setLockTimer] = useState(0);
//   const [mpinLoading, setMpinLoading] = useState(false);

//   const newRefs = useRef([]);
//   const confirmRefs = useRef([]);

//   const [activeSession, setActiveSession] = useState(false);

//   // =========================
//   // OTP TIMER
//   // =========================
//   useEffect(() => {
//     if (otpTimer <= 0) return;
//     const interval = setInterval(() => {
//       setOtpTimer((prev) => prev - 1);
//     }, 1000);
//     return () => clearInterval(interval);
//   }, [otpTimer]);

//   // =========================
//   // SESSION CHECK
//   // =========================
//   const checkSession = async () => {
//     if (!identifier) return;
//     try {
//       const res = await axios.get(
//         `${BASE_URL}common/check-session/${identifier}`
//       );
//       setActiveSession(res.data?.active === true);
//     } catch {}
//   };

//  const handleBoxChange = (e, index, type = "new") => {
//   const value = e.target.value.replace(/\D/g, "");

//   if (!value) return;

//   const updated =
//     type === "new" ? [...newMpin] : [...confirmMpin];

//   updated[index] = value[0];

//   type === "new"
//     ? setNewMpin(updated)
//     : setConfirmMpin(updated);

//   // Move to next input
//   if (index < 3) {
//     const nextRef =
//       type === "new"
//         ? newRefs.current[index + 1]
//         : confirmRefs.current[index + 1];

//     nextRef?.focus();
//   }
// };

// const handleKeyDown = (e, index, type = "new") => {
//   if (e.key === "Backspace") {
//     const updated =
//       type === "new" ? [...newMpin] : [...confirmMpin];

//     if (updated[index]) {
//       updated[index] = "";
//     } else if (index > 0) {
//       const prevRef =
//         type === "new"
//           ? newRefs.current[index - 1]
//           : confirmRefs.current[index - 1];

//       prevRef?.focus();
//     }

//     type === "new"
//       ? setNewMpin(updated)
//       : setConfirmMpin(updated);
//   }
// };

//   // =========================
//   // REDIRECT
//   // =========================
//   // const redirectBasedOnRole = (userData) => {
//   //   const role = Number(userData.role);
//   //   const routes = {
//   //     1: "/superdashboard",
//   //     2: "/dashboard",
//   //     3: "/empdashboard",
//   //     4: "/agentdashboard",
//   //     5: "/retailerdashboard",
//   //     6: "/userdashboard",
//   //   };
//   //   navigate(routes[role] || "/dashboard");
//   // };

//     const getRoleRoute = (role) => {
//     const map = {
//       1: "admin",
//       2: "company",
//       4: "agent",
//       3: "employee",
//       5: "retailer",
//       6: "retailer_user",
//     };
//     return map[Number(role)] || "company";
//   };

//   const redirectBasedOnRole = (userData) => {
//     const role = Number(userData.role);
//     const type = userData.type_of_user;

//     // 🔥 SPECIAL CASE
//     if (role === 3) {
//       const map = {
//         Operation: "/oprdashboard",
//         Sales: "/empdashboard",
//       };
//       navigate(map[type] || "/empdashboard");
//       return;
//     }

//     // 🔁 DEFAULT ROLE ROUTES
//     const routes = {
//       1: "/superdashboard",
//       2: "/dashboard",
//       4: "/agentdashboard",
//       3: "/empdashboard",
//       5: "/retailerdashboard",
//       6: "/userdashboard",
//     };

//     navigate(routes[role] || "/dashboard");
//   };


//   // useEffect(() => {
//   //   if (user && user.role) {
//   //     redirectBasedOnRole(getRoleRoute(user.role));
//   //   }
//   // }, [user]);

//   const validate = () => {
//     let ok = true;
//     let idErr = "";
//     let pwdErr = "";

//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
//     const phoneRe = /^[0-9]{10}$/;

//     if (!identifier.trim()) {
//       idErr = "Enter email or phone";
//       ok = false;
//     } else if (!(emailRe.test(identifier) || phoneRe.test(identifier))) {
//       idErr = "Use valid email or 10-digit phone";
//       ok = false;
//     }

//     if (!password) {
//       pwdErr = "Enter password";
//       ok = false;
//     } else if (password.length < 3) {
//       pwdErr = "Min 3 characters";
//       ok = false;
//     }

//     setErrors({ id: idErr, pwd: pwdErr });
//     return ok;
//   };

//   // =========================
//   // PASSWORD LOGIN
//   // =========================
//  const handleSubmit = async (e) => {
//   e.preventDefault();
//   if (!validate()) return;

//   setSubmitting(true);
//   try {
//     const res = await axios.post(`${BASE_URL}common/login`, {
//       email: identifier,
//       password,
//     });

//     const userData = res.data.data;

//     // 🔍 CHECK FORCE LOGOUT STATUS
//     // 🔍 CHECK FORCE LOGOUT STATUS (ONLY FOR RETAILER)
//       if (Number(userData.role) === 5) {
//         const statusRes = await axios.get(
//           `${BASE_URL}common/logout-status/${userData.id}`
//         );

//         const isForceLogout =
//           statusRes.data?.is_logout === true ||
//           statusRes.data?.is_logout === 1;

//         if (statusRes.data?.status === true && isForceLogout) {
//           setForceLogoutPopup(true);
//           return;
//         }
//       }

//     // ✅ ALLOW LOGIN
//     login(userData);

//     if (!userData.is_mpin_active !==1) {
//       setShowSetMpinModal(true); // new state
//       return;
//     }

//     redirectBasedOnRole(userData);

//   } catch (err) {
//     toast.error(err?.response?.data?.message || "Login failed");
//   } finally {
//     setSubmitting(false);
//   }
// };

// const handleForgotPassword = async () => {
//   if (!forgotEmail.trim()) {
//     setForgotMsg("Please enter your email");
//     return;
//   }

//   setForgotLoading(true);
//   setForgotMsg("");

//   try {
//     await axios.post(`${BASE_URL}warranty/company/password/forgot`, {
//       email: forgotEmail,
//     });

//     // ✅ SUCCESS TOAST
//     toast.success("Reset mail sent successfully");

//     // ✅ CLOSE MODAL
//     setForgotOpen(false);
//     setForgotEmail("");
//     setForgotMsg("");

//   } catch (err) {
//     setForgotMsg(
//       err?.response?.data?.message || "Failed to send reset link"
//     );
//   } finally {
//     setForgotLoading(false);
//   }
// };

//   // =========================
//   // OTP
//   // =========================
//   const sendOtp = async () => {
//     if (!identifier) return toast.error("Enter email");

//     setOtpLoading(true);
//     try {
//       await axios.post(`${BASE_URL}common/send-otp`, {
//         login_value: identifier, // ✅ correct payload key
//       });

//       toast.success("OTP sent successfully");
//       setOtpTimer(60);
//       setOtpSent(true);
//     } catch (err) {
//       toast.error("Failed to send OTP");
//     } finally {
//       setOtpLoading(false);
//     }
//   };

//   const verifyOtp = async () => {
//   try {
//     const res = await axios.post(`${BASE_URL}common/verify-otp`, {
//       login_value: identifier,
//       otp,
//     });

//     const userData = res.data.data;

//     login(userData);

//     // 🔥 ONLY CHECK IF MPIN EXISTS
//     if (!userData.is_mpin_active !== 1) {
//       setShowSetMpinModal(true);
//       return;
//     }

//     // ✅ If MPIN already exists → go directly
//     redirectBasedOnRole(userData);

//   } catch {
//     toast.error("Invalid OTP");
//   }
// };

//   // =========================
//   // MPIN
//   // =========================
  

//   const handleSubmitMpin = async () => {
//   const mpinValue = newMpin.join("");
//   const confirmValue = confirmMpin.join("");

//   if (mpinValue.length !== 4)
//     return toast.error("Enter 4 digit MPIN");

//   if (confirmValue.length !== 4)
//     return toast.error("Confirm your MPIN");

//   if (mpinValue !== confirmValue)
//     return toast.error("MPIN does not match");

//   try {
//     await axios.post(`${BASE_URL}common/set-mpin`, {
//       company_id: user?.id,
//       m_pin: mpinValue,
//     });

//     toast.success("MPIN set successfully");
//     setShowSetMpinModal(false);
//     redirectBasedOnRole(user);

//   } catch (err) {
//     toast.error("Failed to set MPIN");
//   }
// };

// useEffect(() => {
//   if (lockTimer <= 0) return;

//   const interval = setInterval(() => {
//     setLockTimer((prev) => prev - 1);
//   }, 1000);

//   return () => clearInterval(interval);
// }, [lockTimer]);

// const handleMpinLogin = async () => {
//   if (!identifier)
//     return toast.error("Enter Company ID");

//   if (mpin.length !== 4)
//     return toast.error("Enter 4 digit MPIN");

//   if (mpinLocked)
//     return toast.error(`Account locked. Try after ${lockTimer}s`);

//   try {
//     setMpinLoading(true);

//     const res = await axios.post(
//       `${BASE_URL}common/login-mpin`,
//       {
//         company_id: identifier,
//         m_pin: mpin,
//       }
//     );

//     if (res.data?.status) {
//       // success
//       login(res.data.data);
//       redirectBasedOnRole(res.data.data);
//     } else {
//       handleMpinError(res.data);
//     }

//   } catch (err) {
//     handleMpinError(err?.response?.data);
//   } finally {
//     setMpinLoading(false);
//   }
// };

// const handleMpinError = (data) => {
//   const attemptsLeft = data?.attempts_left ?? 0;

//   if (data?.lock_time) {
//     setMpinLocked(true);
//     setLockTimer(data.lock_time);
//     toast.error(`Account locked for ${data.lock_time} seconds`);
//     return;
//   }

//   setMpinAttempts((prev) => prev + 1);

//   toast.error(
//     attemptsLeft > 0
//       ? `Invalid MPIN. ${attemptsLeft} attempts left`
//       : "Invalid MPIN"
//   );

//   setMpin("");
// };

//   // =========================
//   // GOOGLE LOGIN
//   // =========================
//   const handleGoogleLogin = async (credentialResponse) => {
//   try {
//     const token = credentialResponse?.credential;

//     if (!token) {
//       toast.error("Google token missing");
//       return;
//     }

//     const res = await axios.post(
//       `${BASE_URL}warranty/company/auth/google`,
//       { token }
//     );

//     if (res.data?.status) {
//       login(res.data.data);
//       redirectBasedOnRole(res.data.data);
//     } else {
//       toast.error(res.data?.message || "Google login failed");
//     }
//   } catch (err) {
//     toast.error(
//       err?.response?.data?.message || "Google login failed"
//     );
//   }
// };

//   return (
//     <AuthLayout showIllustration={true}>
//       <GlassCard className="w-full max-w-md mx-auto px-6 py-8 sm:px-8 sm:py-10 backdrop-blur-xl bg-white/70 shadow-2xl border border-white/40">
//       <AuthHeader
// //           // title="Login"
// //           // subtitle="Access your dashboard"
//         />
//         <div className="text-center mb-6">
//           <h1 className="text-3xl font-bold text-slate-800">
//             Welcome Back
//           </h1>
//           <p className="text-sm text-slate-500 mt-1">
//             Sign in to continue
//           </p>
//         </div>

//         {/* AUTH MODE TABS */}
//         <div className="mb-6 flex rounded-xl bg-slate-100 p-1">
//           {["password","otp", "mpin"].map((mode) => (
//             <button
//               key={mode}
//               type="button"
//               onClick={() => {
//                 setAuthMode(mode);
//                 setOtpSent(false);
//                 setOtp("");
//               }}
//               className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${
//                 authMode === mode
//                   ? "bg-white shadow text-blue-600"
//                   : "text-slate-500"
//               }`}
//             >
//               {mode.toUpperCase()}
//             </button>
//           ))}
//         </div>

//         <form onSubmit={handleSubmit} className="space-y-4">
//           {/* IDENTIFIER */}
//           <div>
//             <div className="relative rounded-xl bg-white border px-3 focus-within:ring-2 focus-within:ring-blue-400">
//               <span className="absolute left-3 top-3 text-slate-400">
//                 {/^[0-9]/.test(identifier) ? (
//                   <Phone size={16} />
//                 ) : (
//                   <Mail size={16} />
//                 )}
//               </span>
//               <input
//                 value={identifier}
//                 onChange={(e) => setIdentifier(e.target.value)}
//                 onBlur={checkSession}
//                 placeholder="Email or phone"
//                 className="w-full bg-transparent pl-6 py-3 text-sm outline-none"
//               />
//             </div>

//             {activeSession && (
//               <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
//                 Active session detected on another device
//               </div>
//             )}
//           </div>

//           {/* ANIMATED AUTH MODES */}
//           <AnimatePresence mode="wait">
//             <motion.div
//               key={authMode}
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               exit={{ opacity: 0, y: -10 }}
//               transition={{ duration: 0.25 }}
//             >
//               {authMode === "password" && (
//                 <>
//                   <div className="relative rounded-xl bg-white border px-3">
//                     <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
//                     <input
//                       type={showPwd ? "text" : "password"}
//                       value={password}
//                       onChange={(e) => setPassword(e.target.value)}
//                       placeholder="Password"
//                       className="w-full pl-6 pr-8 py-3 outline-none text-sm"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => setShowPwd(!showPwd)}
//                       className="absolute right-2 top-2"
//                     >
//                       {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//                     </button>
//                   </div>

//                   <button
//                     type="submit"
//                     disabled={submitting}
//                     className="w-full rounded-xl py-3 font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:scale-[1.02] transition-all mt-3"
//                   >
//                     {submitting ? "Logging in..." : "Login"}
//                   </button>

//                   <div className="text-right">
//                   <button
//                     type="button"
//                     onClick={() => {
//                       setForgotOpen(true);
//                       setForgotMsg("");
//                       setForgotEmail("");
//                     }}
//                     className="text-xs font-semibold text-blue-600 hover:underline"
//                   >
//                     Forgot password?
//                   </button>
//                 </div>
//                 </>
//               )}

//               {authMode === "otp" && (
//                 <>
//                   {!otpSent ? (
//                     <>
                    
//                     <button
//                       type="button"
//                       onClick={sendOtp}
//                       disabled={otpLoading}
//                       className="w-full rounded-xl py-3 bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
//                     >
//                       {otpLoading ? "Sending OTP..." : "Send OTP"}
//                     </button>
//                     {/* <div className="text-right">
//                         <button
//                           type="button"
//                           onClick={() => {
//                             setForgotOpen(true);
//                             setForgotMsg("");
//                             setForgotEmail("");
//                           }}
//                           className="text-xs font-semibold text-blue-600 hover:underline"
//                         >
//                           Forgot password?
//                         </button>
//                       </div> */}
//                       </>
//                   ) : (
//                     /* STEP 2: SHOW VERIFY OTP */
//                     <>
//                       <input
//                         value={otp}
//                         onChange={(e) => setOtp(e.target.value)}
//                         placeholder="Enter 6-digit OTP"
//                         className="w-full rounded-xl border px-3 py-3 text-center tracking-widest text-lg"
//                       />

//                       <button
//                         type="button"
//                         onClick={verifyOtp}
//                         className="w-full mt-3 rounded-xl bg-blue-600 py-3 text-white font-semibold hover:bg-blue-700 transition"
//                       >
//                         {/* {ismail=== 1 ? "Verify Mail OTP" : "Verify WA OTP"} */}
//                         Verify OTP
//                       </button>

//                       {/* RESEND TIMER */}
//                       <div className="text-xs text-slate-500 mt-3 text-center">
//                         {otpTimer > 0 ? (
//                           <span>Resend OTP in {otpTimer}s</span>
//                         ) : (
//                           <button
//                             type="button"
//                             onClick={sendOtp}
//                             className="text-blue-600 font-semibold hover:underline"
//                           >
//                             Resend OTP
//                           </button>
//                         )}
//                       </div>
                      
//                     </>
//                   )}
//                 </>
//               )}

//               {authMode === "mpin" && (
//                 <>
//                   <input
//                     value={identifier}
//                     onChange={(e) => setIdentifier(e.target.value)}
//                     placeholder="Enter Company ID"
//                     className="w-full rounded-xl border px-3 py-3 text-sm mb-3"
//                   />

//                   <input
//                     type="password"
//                     maxLength={4}
//                     value={mpin}
//                     onChange={(e) =>
//                       setMpin(e.target.value.replace(/\D/g, ""))
//                     }
//                     placeholder="••••"
//                     className="w-full rounded-xl border px-3 py-4 text-center text-2xl tracking-[12px]"
//                   />

//                   {mpinLocked && (
//                     <p className="text-sm text-rose-600 mt-2">
//                       Account locked. Try after {lockTimer}s
//                     </p>
//                   )}

//                   <button
//                     type="button"
//                     onClick={handleMpinLogin}
//                     disabled={mpinLoading || mpinLocked}
//                     className="w-full mt-3 rounded-xl bg-blue-600 py-3 text-white font-semibold disabled:opacity-50"
//                   >
//                     {mpinLoading ? "Verifying..." : "Login with MPIN"}
//                   </button>
//                 </>
//               )}
//             </motion.div>
//           </AnimatePresence>
//         </form>

//         {/* DIVIDER */}
//         <div className="my-6 flex items-center gap-3">
//           <div className="flex-1 h-px bg-slate-200" />
//           <span className="text-xs text-slate-400">
//             OR CONTINUE WITH
//           </span>
//           <div className="flex-1 h-px bg-slate-200" />
//         </div>

//         {/* CUSTOM GOOGLE BUTTON */}
//         <GoogleLogin
//           onSuccess={handleGoogleLogin}
//           onError={() => toast.error("Google login failed")}
//           useOneTap
//         />
//       </GlassCard>

//       {forgotOpen && (
//           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
//             <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
              
//               <h3 className="text-lg font-semibold text-slate-800">
//                 Forgot Password
//               </h3>
//               <p className="text-xs text-slate-500 mt-1">
//                 Enter your registered email to receive a reset link
//               </p>

//               <div className="mt-4">
//                 <label className="text-sm font-medium text-slate-700">
//                   Email
//                 </label>
//                 <input
//                   type="email"
//                   value={forgotEmail}
//                   onChange={(e) => setForgotEmail(e.target.value)}
//                   placeholder="you@example.com"
//                   className="
//                     mt-1 w-full rounded-xl border border-slate-200
//                     px-3 py-2 text-sm outline-none
//                     focus:ring-2 focus:ring-blue-400
//                   "
//                 />
//               </div>

//               {forgotMsg && (
//                 <p
//                   className={`mt-2 text-xs font-semibold ${
//                     forgotMsg.includes("sent")
//                       ? "text-emerald-600"
//                       : "text-rose-500"
//                   }`}
//                 >
//                   {forgotMsg}
//                 </p>
//               )}

//               <div className="mt-5 flex justify-end gap-2">
//                 <button
//                   type="button"
//                   onClick={() => setForgotOpen(false)}
//                   className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
//                 >
//                   Cancel
//                 </button>

//                 <button
//                   type="button"
//                   onClick={handleForgotPassword}
//                   disabled={forgotLoading}
//                   className="
//                     rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white
//                     hover:bg-blue-700 disabled:opacity-60
//                   "
//                 >
//                   {forgotLoading ? "Sending..." : "Send link"}
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}

//       {forceLogoutPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
//           <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
//             <h2 className="text-lg font-bold text-rose-700">
//               Login Restricted
//             </h2>

//             <p className="mt-2 text-sm font-semibold text-slate-600">
//               You have been <span className="font-bold text-rose-600">force logged out</span> by your company.
//               <br />
//               Please contact support for assistance.
//             </p>

//             <button
//               onClick={() => setForceLogoutPopup(false)}
//               className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700"
//             >
//               OK
//             </button>
//           </div>
//         </div>
//       )}

//       {showSetMpinModal && (
//   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
//     <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl">

//       {/* Title */}
//       <h2 className="text-center text-xl font-semibold text-slate-800">
//         Set Your MPIN
//       </h2>

//       {/* ENTER MPIN */}
//       <div className="mt-8">
//         <p className="text-sm text-slate-600 mb-4 text-center">
//           Enter MPIN
//         </p>

//         <div className="flex justify-center gap-4">
//           {newMpin.map((digit, index) => (
//             <input
//               key={index}
//               ref={(el) => (newRefs.current[index] = el)}
//               type="tel"
//               inputMode="numeric"
//               pattern="[0-9]*"
//               maxLength={1}
//               value={digit}
//               onChange={(e) => handleBoxChange(e, index, "new")}
//               onKeyDown={(e) => handleKeyDown(e, index, "new")}
//               className="w-14 h-14 border border-slate-300 rounded-lg text-center text-xl font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
//             />
//           ))}
//         </div>
//       </div>

//       {/* CONFIRM MPIN */}
//       <div className="mt-8">
//         <p className="text-sm text-slate-600 mb-4 text-center">
//           Confirm MPIN
//         </p>

//         <div className="flex justify-center gap-4">
//           {confirmMpin.map((digit, index) => (
//             <input
//               key={index}
//               ref={(el) => (confirmRefs.current[index] = el)}
//               type="tel"
//               inputMode="numeric"
//               pattern="[0-9]*"
//               maxLength={1}
//               value={digit}
//               onChange={(e) => handleBoxChange(e, index, "confirm")}
//               onKeyDown={(e) => handleKeyDown(e, index, "confirm")}
//               className="w-14 h-14 border border-slate-300 rounded-lg text-center text-xl font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
//             />
//           ))}
//         </div>
//       </div>

//       {/* BUTTON */}
//       <button
//         onClick={handleSubmitMpin}
//         className="mt-10 w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
//       >
//         Set MPIN
//       </button>
//     </div>
//   </div>
// )}
//     </AuthLayout>
//   );
// }

// ***************************************************working code************************************************************8


